import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="alert-form"
// Handles dynamic form updates for alert conditions
export default class extends Controller {
  static targets = [
    "routeAggregateFields",
    "choiceSelector",
    "aggregationMethodContainer",
    "aggregationMethod",
    "thresholdContainer",
    "thresholdLabel",
    "thresholdInput",
    "thresholdHint",
    "betweenFields",
  ];

  connect() {
    this.initializeConditionTypeVisibility();
  }

  // Get field metadata from the data attribute
  getFieldsMetadata() {
    if (!this.hasRouteAggregateFieldsTarget) return {};
    try {
      return JSON.parse(
        this.routeAggregateFieldsTarget.dataset.fieldsMetadata || "{}"
      );
    } catch {
      return {};
    }
  }

  // Get aggregation method translations from the data attribute
  getAggregationTranslations() {
    if (!this.hasRouteAggregateFieldsTarget) return {};
    try {
      return JSON.parse(
        this.routeAggregateFieldsTarget.dataset.aggregationTranslations || "{}"
      );
    } catch {
      return {};
    }
  }

  // Checkbox fields relabel "average"/"sum" as incidence vs case count
  getCheckboxAggregationTranslations() {
    if (!this.hasRouteAggregateFieldsTarget) return {};
    try {
      return JSON.parse(
        this.routeAggregateFieldsTarget.dataset.checkboxAggregationTranslations ||
          "{}"
      );
    } catch {
      return {};
    }
  }

  // "sum" is an absolute case count, so it is never shown as a percentage.
  // Mirrors Alert::EvaluationFormatting.percentage_display?
  isPercentageThreshold({ isCheckbox, isMultipleChoice, isTreePresence }, method) {
    if (isTreePresence && method === "tree_presence_percentage") return true;
    if (method === "sum") return false;
    return Boolean(isCheckbox || isMultipleChoice);
  }

  // Get choice selector translations from the data attribute
  getChoiceSelectorTranslations() {
    if (!this.hasRouteAggregateFieldsTarget) return {};
    try {
      return JSON.parse(
        this.routeAggregateFieldsTarget.dataset.choiceSelectorTranslations ||
          "{}"
      );
    } catch {
      return {};
    }
  }

  initializeConditionTypeVisibility() {
    const conditionTypeSelect = this.element.querySelector(
      '[name="alert[conditions][type]"]'
    );
    const conditionType = conditionTypeSelect?.value;

    if (!conditionTypeSelect) return;

    if (this.hasRouteAggregateFieldsTarget) {
      const shouldShow =
        conditionType === "route_aggregate" ||
        (conditionTypeSelect.options.length === 1 &&
          conditionTypeSelect.options[0].value === "route_aggregate");

      this.routeAggregateFieldsTarget.classList.toggle("hidden", !shouldShow);
    }
  }

  handleConditionTypeChange(event) {
    const conditionType = event?.target?.value;
    if (!event || !this.hasRouteAggregateFieldsTarget) return;

    this.routeAggregateFieldsTarget.classList.toggle(
      "hidden",
      conditionType !== "route_aggregate"
    );
  }

  handleEvaluationModeChange() {
    if (!this.hasAggregationMethodTarget) return;

    const customFieldSelect = this.element.querySelector(
      '[name="alert[conditions_attributes][custom_field_id]"], [name="alert[conditions][custom_field_id]"]'
    );
    const customFieldId = customFieldSelect?.value;
    const fieldsMetadata = this.getFieldsMetadata();
    const fieldData = fieldsMetadata[customFieldId];

    if (!fieldData) {
      this.resetAggregationMethod();
      this.updateThresholdDisplay(false);
      return;
    }

    const validAggregationMethods = this.validAggregationMethodsFor(fieldData);
    this.setAggregationMethods(
      validAggregationMethods,
      this.defaultAggregationMethod(fieldData, validAggregationMethods)
    );
    this.handleAggregationMethodChange({ target: this.aggregationMethodTarget });
  }

  handleCustomFieldChange(event) {
    const customFieldId = event?.target?.value;
    const fieldsMetadata = this.getFieldsMetadata();
    const fieldData = fieldsMetadata[customFieldId];

    if (!fieldData) {
      this.hideChoiceSelector();
      this.resetAggregationMethod();
      this.updateThresholdDisplay(false);
      return;
    }

    const isMultipleChoice = fieldData.multiple_choice === true;
    const isCheckbox = fieldData.checkbox === true;
    const isTreePresence = fieldData.tree_presence === true;
    const validAggregationMethods = this.validAggregationMethodsFor(fieldData);

    // Update field type flags on threshold container for use in aggregation method change handler
    if (this.hasThresholdContainerTarget) {
      this.thresholdContainerTarget.dataset.isCheckbox = isCheckbox
        ? "true"
        : "false";
      this.thresholdContainerTarget.dataset.isMultipleChoice = isMultipleChoice
        ? "true"
        : "false";
      this.thresholdContainerTarget.dataset.isTreePresence = isTreePresence
        ? "true"
        : "false";
    }

    const defaultMethod = this.defaultAggregationMethod(
      fieldData,
      validAggregationMethods
    );

    if (isMultipleChoice) {
      this.showChoiceSelector(fieldData.choices);
    } else {
      this.hideChoiceSelector();
    }

    this.setAggregationMethods(
      validAggregationMethods,
      defaultMethod,
      isCheckbox
    );
    this.updateThresholdDisplay(
      this.isPercentageThreshold(
        { isCheckbox, isMultipleChoice, isTreePresence },
        this.hasAggregationMethodTarget
          ? this.aggregationMethodTarget.value
          : defaultMethod
      )
    );
  }

  handleAggregationMethodChange(event) {
    const aggregationMethod = event?.target?.value;

    // Check if current field is a checkbox or multiple choice
    const isCheckbox =
      this.hasThresholdContainerTarget &&
      this.thresholdContainerTarget.dataset.isCheckbox === "true";
    const isMultipleChoice =
      this.hasThresholdContainerTarget &&
      this.thresholdContainerTarget.dataset.isMultipleChoice === "true";
    const isTreePresence =
      this.hasThresholdContainerTarget &&
      this.thresholdContainerTarget.dataset.isTreePresence === "true";

    this.updateThresholdDisplay(
      this.isPercentageThreshold(
        { isCheckbox, isMultipleChoice, isTreePresence },
        aggregationMethod
      )
    );
  }

  handleOperatorChange(event) {
    const operator = event?.target?.value;

    if (this.hasBetweenFieldsTarget) {
      this.betweenFieldsTarget.classList.toggle(
        "hidden",
        operator !== "between"
      );
    }
  }

  // Update threshold field label, placeholder, and hint visibility
  updateThresholdDisplay(isPercentage) {
    if (!this.hasThresholdContainerTarget) return;

    const container = this.thresholdContainerTarget;

    // Update label
    if (this.hasThresholdLabelTarget) {
      this.thresholdLabelTarget.textContent = isPercentage
        ? container.dataset.thresholdLabelPercentage
        : container.dataset.thresholdLabelValue;
    }

    // Update input attributes
    if (this.hasThresholdInputTarget) {
      const input = this.thresholdInputTarget;
      input.placeholder = isPercentage ? "10" : "0";
      if (isPercentage) {
        input.min = 0;
        input.max = 100;
      } else {
        input.removeAttribute("min");
        input.removeAttribute("max");
      }
    }

    // Update hint visibility
    if (this.hasThresholdHintTarget) {
      this.thresholdHintTarget.classList.toggle("hidden", !isPercentage);
    }
  }

  showChoiceSelector(choices) {
    if (!this.hasChoiceSelectorTarget) return;

    const choiceSelect = this.choiceSelectorTarget.querySelector("select");
    if (!choiceSelect) return;

    // Get translated prompt text
    const translations = this.getChoiceSelectorTranslations();
    const promptText =
      translations.select_choice || "Select a choice to monitor";

    const options = [new Option(promptText, "")];
    choices.forEach((choice) => {
      options.push(new Option(choice.label, choice.value));
    });
    choiceSelect.replaceChildren(...options);

    this.choiceSelectorTarget.classList.remove("hidden");
    choiceSelect.disabled = false;
  }

  hideChoiceSelector() {
    if (!this.hasChoiceSelectorTarget) return;
    this.choiceSelectorTarget.classList.add("hidden");
    const choiceSelect = this.choiceSelectorTarget.querySelector("select");
    if (choiceSelect) {
      choiceSelect.value = "";
      choiceSelect.disabled = true;
    }
  }

  // Set aggregation methods based on valid methods for the field type
  setAggregationMethods(validMethods, defaultValue = null, isCheckbox = false) {
    if (!this.hasAggregationMethodTarget) return;
    const select = this.aggregationMethodTarget;

    // Get translations from data attribute, with fallbacks
    const translations = {
      ...this.getAggregationTranslations(),
      ...(isCheckbox ? this.getCheckboxAggregationTranslations() : {}),
    };
    const fallbackOptions = {
      sum: "Sum",
      average: "Average",
      median: "Median",
      min: "Minimum",
      max: "Maximum",
      count: "Count",
      tree_presence_percentage: "Tree presence %",
      value: "Route value",
    };

    // Filter to only valid methods for this field type
    const allMethods = Object.keys(fallbackOptions);
    const methods = validMethods.length > 0 ? validMethods : allMethods;

    const options = methods.map(
      (method) =>
        new Option(
          translations[method] || fallbackOptions[method] || method,
          method
        )
    );
    select.replaceChildren(...options);

    // Set default value if provided and valid
    if (defaultValue && methods.includes(defaultValue)) {
      select.value = defaultValue;
    } else if (methods.length === 1) {
      select.value = methods[0];
    }
  }

  // Legacy methods for backwards compatibility
  setAggregationMethodToCount() {
    this.setAggregationMethods(["count"], "count");
  }

  resetAggregationMethod() {
    this.setAggregationMethods([
      "sum",
      "average",
      "median",
      "min",
      "max",
      "count",
    ]);
  }

  validAggregationMethodsFor(fieldData) {
    const methodsByMode = fieldData.valid_aggregation_methods_by_mode || {};
    const methods =
      methodsByMode[this.evaluationMode()] ||
      fieldData.valid_aggregation_methods ||
      [];
    return Array.isArray(methods) ? methods : [];
  }

  evaluationMode() {
    const evaluationModeSelect = this.element.querySelector(
      '[name="alert[evaluation_mode]"]'
    );
    return evaluationModeSelect?.value || "realtime";
  }

  defaultAggregationMethod(fieldData, validMethods) {
    if (validMethods.includes("value")) return "value";
    if (fieldData.checkbox === true) {
      return validMethods.includes("average") ? "average" : "sum";
    }
    if (fieldData.multiple_choice === true) return "average";
    return null;
  }
}
